import React, { useMemo, useState } from "react";
import { PageHeader } from "../components/ui-bj/Primitives";
import { Loading, ErrorState } from "../components/ui-bj/States";
import { useAirtable } from "../hooks/useAirtable";
import { num, arr, indexById } from "../lib/airtableHelpers";
import ExportButtons from "../components/ui-bj/ExportButtons";
import PeriodScopeBar, { usePeriodFilter } from "../components/ui-bj/PeriodScopeBar";
import { SERVICE_TABLES } from "../components/client-portfolio/serviceTables";
import { ClientFilters } from "../components/client-portfolio/ClientFilters";
import { ClientTable } from "../components/client-portfolio/ClientTable";
import { ClientDetailPanel } from "../components/client-portfolio/ClientDetailPanel";

const ClientPortfolioPage = () => {
  const { records: clients, loading, error, refetch } = useAirtable("clients");
  const credit = useAirtable("credit");
  const llc = useAirtable("llc");
  const pf = useAirtable("personal-funding");
  const bf = useAirtable("business-funding");
  const tl = useAirtable("tradelines");
  const grants = useAirtable("grants");

  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("All");
  const [pkg, setPkg] = useState("All");
  const [service, setService] = useState("All");
  const [activeId, setActiveId] = useState(null);
  const { period, setPeriod, scope, setScope, filterByDate } = usePeriodFilter("all");

  const distinct = (key) => {
    const s = new Set();
    clients.forEach((c) => {
      const v = c.fields?.[key];
      if (v) {
        if (Array.isArray(v)) v.forEach((x) => s.add(x));
        else s.add(v);
      }
    });
    return ["All", ...Array.from(s).sort()];
  };

  const statusOpts = useMemo(() => distinct("Client Status"), [clients]);
  const pkgOpts = useMemo(() => distinct("Service Package"), [clients]);
  const svcOpts = useMemo(() => distinct("Active Services"), [clients]);

  const serviceRecords = useMemo(() => ({
    credit: credit.records,
    llc: llc.records,
    "personal-funding": pf.records,
    "business-funding": bf.records,
    tradelines: tl.records,
    grants: grants.records,
  }), [credit.records, llc.records, pf.records, bf.records, tl.records, grants.records]);

  const indexes = useMemo(() => {
    const out = {};
    for (const t of SERVICE_TABLES) out[t.slug] = indexById(serviceRecords[t.slug]);
    return out;
  }, [serviceRecords]);

  const filtered = useMemo(() => {
    return clients.filter((c) => {
      const f = c.fields || {};
      if (!filterByDate(f["Date Joined"])) return false;
      const blob = `${f["Full Name"] || ""} ${f["Client ID"] || ""} ${f["Consultant Assigned"] || ""} ${f["Email"] || ""}`.toLowerCase();
      if (query && !blob.includes(query.toLowerCase())) return false;
      if (status !== "All" && f["Client Status"] !== status) return false;
      if (pkg !== "All" && f["Service Package"] !== pkg) return false;
      if (service !== "All" && !arr(f["Active Services"]).includes(service)) return false;
      return true;
    });
  }, [clients, query, status, pkg, service, filterByDate]);

  const active = activeId ? clients.find((c) => c.id === activeId) : null;
  const serviceSummaries = useMemo(() => {
    if (!active) return [];
    return SERVICE_TABLES.map((t) => {
      const links = arr(active.fields?.[t.linkField]);
      const recs = links.map((id) => indexes[t.slug].get(id)).filter(Boolean);
      const first = recs[0];
      const f = first?.fields || {};
      return {
        label: t.label,
        count: recs.length,
        status: f[t.statusField] || (recs.length ? "Active" : "Not Started"),
        detail: f[t.detailField] !== undefined ? `${f[t.detailField]} ${t.detailLabel}` : "—",
      };
    });
  }, [active, indexes]);

  if (loading) return <Loading label="Loading clients" />;
  if (error) return <ErrorState message={error} onRetry={refetch} />;

  return (
    <div data-testid="client-portfolio-page">
      <PageHeader
        eyebrow="DASHBOARD / CLIENT PORTFOLIO"
        title="Client Portfolio"
        subtitle={`${filtered.length} of ${clients.length} clients under management • ${scope === "period" ? "filtered by Date Joined" : "all-time view"} • Click any row for the full service record.`}
        right={
          <div className="flex items-center gap-3 flex-wrap">
            <PeriodScopeBar scope={scope} setScope={setScope} period={period} setPeriod={setPeriod} testidPrefix="clients-scope" />
            <ExportButtons
              title="Client Portfolio"
              filename="clients"
              testidPrefix="export-clients"
              columns={[
                { key: "id", label: "Client ID", get: (c) => c.fields?.["Client ID"] },
                { key: "name", label: "Full Name", get: (c) => c.fields?.["Full Name"] },
                { key: "package", label: "Package", get: (c) => c.fields?.["Service Package"] },
                { key: "services", label: "Active Services", get: (c) => (c.fields?.["Active Services"] || []).join("; ") },
                { key: "status", label: "Status", get: (c) => c.fields?.["Client Status"] },
                { key: "payment", label: "Payment", get: (c) => c.fields?.["Payment Status"] },
                { key: "joined", label: "Date Joined", get: (c) => c.fields?.["Date Joined"] },
                { key: "days", label: "Days as Client", get: (c) => num(c.fields?.["Days as Client"]) },
                { key: "appt", label: "Next Appt", get: (c) => c.fields?.["Next Appointment"] },
                { key: "consultant", label: "Consultant", get: (c) => c.fields?.["Consultant Assigned"] },
              ]}
              rows={filtered}
            />
          </div>
        }
      />

      <ClientFilters
        query={query} setQuery={setQuery}
        status={status} setStatus={setStatus} statusOpts={statusOpts}
        pkg={pkg} setPkg={setPkg} pkgOpts={pkgOpts}
        service={service} setService={setService} svcOpts={svcOpts}
        showing={filtered.length}
      />

      <div className={`grid ${active ? "grid-cols-1 xl:grid-cols-3" : "grid-cols-1"} gap-5`}>
        <ClientTable clients={filtered} activeId={active?.id} onRowClick={setActiveId} wide={!active} />
        {active && (
          <ClientDetailPanel
            client={active}
            serviceSummaries={serviceSummaries}
            onClose={() => setActiveId(null)}
          />
        )}
      </div>
    </div>
  );
};

export default ClientPortfolioPage;
